import { useCallback, useRef, useState } from "react";
import { useNotification } from "@context/NotificationContext";
import { drybulkCargoOperationsService } from "@services/drybulkCargoOperationsApi";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { runTransaction } from "@telemetry/runTransaction";
import { useTranslation } from "react-i18next";
import {
  DRYBULK_CARGO_OPS_LANG_BASE,
  DRYBULK_CARGO_OPS_QUERY_KEYS,
  DRYBULK_CARGO_OPS_TELEMETRY,
} from "../constants";
import { problemMessage, problemStatus } from "../utils/problemDetail";

const L = `${DRYBULK_CARGO_OPS_LANG_BASE}.readiness`;

/**
 * DBCO-0040 (R-17) - the Shipper confirms its own readiness, one business transaction. On success the detail is re-read and
 * the grid refreshed; any refusal is returned as `dialogError` so the confirmation dialog can show it and stay open, and a 409
 * (no longer pending readiness) also re-reads the detail. A second call while one is in flight sends nothing.
 */
export const useConfirmReadiness = (operationId: string) => {
  const { t } = useTranslation();
  const { notify } = useNotification();
  const queryClient = useQueryClient();
  const [dialogError, setDialogError] = useState<string | null>(null);
  const inFlight = useRef(false);

  const readDetail = useCallback(
    () =>
      queryClient.fetchQuery({
        queryKey: DRYBULK_CARGO_OPS_QUERY_KEYS.detail(operationId),
        queryFn: () => drybulkCargoOperationsService.getDetail(operationId),
        staleTime: 0,
      }),
    [queryClient, operationId],
  );

  const refreshQuietly = useCallback(async () => {
    try {
      await readDetail();
    } catch {
      // The page's own refresh reads again; nothing here is worth a second message.
    }
  }, [readDetail]);

  const mutation = useMutation({
    mutationFn: (shipperCode: string) =>
      runTransaction(
        {
          module: DRYBULK_CARGO_OPS_TELEMETRY.module,
          screen: DRYBULK_CARGO_OPS_TELEMETRY.operationScreen,
          action: "confirm_cargo_operation_readiness",
          entityType: "cargo_operation",
          entityId: operationId,
        },
        (transactionId) =>
          drybulkCargoOperationsService.confirmReadiness(operationId, shipperCode, {
            transactionId,
          }),
      ),
  });

  /** Resolves true when the readiness was accepted (the dialog closes), false when the dialog must stay open. */
  const confirm = async (shipperCode: string): Promise<boolean> => {
    if (inFlight.current) {
      return false;
    }
    inFlight.current = true;
    setDialogError(null);
    try {
      await mutation.mutateAsync(shipperCode);
    } catch (error) {
      setDialogError(problemMessage(error) ?? t("common.error"));
      if (problemStatus(error) === 409) {
        await refreshQuietly();
      }
      return false;
    } finally {
      inFlight.current = false;
    }
    notify({
      type: "success",
      title: t(`${L}.success.title`),
      description: t(`${L}.success.description`),
    });
    await refreshQuietly();
    void queryClient.invalidateQueries({ queryKey: DRYBULK_CARGO_OPS_QUERY_KEYS.searchAll() });
    return true;
  };

  return {
    confirm,
    isConfirming: mutation.isPending,
    dialogError,
    clearDialogError: () => setDialogError(null),
  };
};
